import React from 'react';
import { Box, Button, Grid, Modal, Typography, Alert } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { BACKGROUND_COLOR, COLOR_PRIMARY_DARK, COLOR_PRIMARY_LIGHT } from 'src/const';
import { FullMarkersFetchTriggerPoint } from 'src/interfaces/idashboard';

const useStyles = makeStyles(theme => ({
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: BACKGROUND_COLOR,
    padding: theme.spacing(4),
    borderRadius: 10,
    outline: 'none',
    [theme.breakpoints.up('xs')]: {
      width: '90%',
      maxWidth: 500,
    },
  },
  button: {
    borderRadius: 5,
    padding: '8px 24px',
    textTransform: 'none',
    fontSize: 16,
  },
  confirmButton: {
    background: COLOR_PRIMARY_DARK,
    color: 'white',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
  },
  cancelButton: {
    color: COLOR_PRIMARY_DARK,
    border: `1px solid ${COLOR_PRIMARY_DARK}`,
    '&:hover': {
      border: `1px solid ${COLOR_PRIMARY_LIGHT}`,
      color: COLOR_PRIMARY_LIGHT,
    },
  },
}));

const getModalContent = (triggerPoint: FullMarkersFetchTriggerPoint | null, marketName: string) => {
  const standardTip = `This may take 20 seconds to 3 minutes, depending on market size.`;

  switch (triggerPoint) {
    case 'filter':
      return {
        info: `To apply your selected filters, we need to process all listings in ${marketName}.`,
        tip: `Performance Tip: Larger markets may take longer to load. If you prefer a quicker response, try using bedroom filters only or a smaller area.`,
      };
    case 'dynamicData':
      return {
        info: `To access this data, we need to process all listings in ${marketName}.`,
        tip: standardTip,
      };
    case 'heatmap':
      return {
        info: `To access this heatmap, we need to process all listings in ${marketName}.`,
        tip: standardTip,
      };
    case 'listingSorting':
      return {
        info: `To sort listings by your selected criteria, we need to process all listings in ${marketName}.`,
        tip: standardTip,
      };
    case 'filterBarShowMore':
      return {
        info: `To see detailed distribution, seasonality, and typical listings for each filter, we need to process all listings in ${marketName}.`,
        tip: standardTip,
      };
    default:
      return {
        info: `We need to process all listings in ${marketName}.`,
        tip: standardTip,
      };
  }
};

const ConfirmFullMarkersFetchModal = ({
  marketName,
  triggerPoint,
  onConfirm,
  onCancel,
  open,
}: {
  marketName: string;
  triggerPoint: FullMarkersFetchTriggerPoint | null;
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
}) => {
  const classes = useStyles();
  const { info, tip } = getModalContent(triggerPoint, marketName);

  return (
    <Modal open={open} onClose={onCancel}>
      <Box className={classes.modalContainer}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {info}
        </Typography>

        <Alert severity="info" sx={{ mb: 4 }}>
          {tip}
        </Alert>

        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button
              variant="outlined"
              onClick={onCancel}
              className={`${classes.button} ${classes.cancelButton}`}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={onConfirm}
              className={`${classes.button} ${classes.confirmButton}`}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default ConfirmFullMarkersFetchModal;
